import { useCallback, useEffect, useMemo, useState } from 'react';

import { LESSON_AUDIO_KIND, loadLessonAudio, type LessonAudioPayload } from '@/lib/lessonAudio';
import { isOnline } from '@/lib/network';
import {
  flush,
  flushOne,
  onOutboxChange,
  onOutboxFailed,
  onOutboxSettled,
  queuedItem,
  queuedOfKind,
  removeQueued,
  type OutboxItem,
} from '@/lib/outbox';
import {
  dismissRejectedRecord,
  listRejectedRecords,
  onRejectedRecordsChange,
  recordQueueRows,
  type RecordQueueRow,
  type RejectedRecord,
} from '@/lib/recordQueue';
import { sendProgressOf, subscribeSendProgress } from '@/lib/sendProgress';

/** Строка списка вместе с ходом отправки: проценты — для полоски под строкой (решение владельца 18.10). */
export type RecordQueueView = RecordQueueRow & { percent: number | null };

export type SendResult = 'sent' | 'offline' | 'queued';

/**
 * Неотправленные записи голоса для профиля и «Домашек» (промпт 22, 18.10.2026).
 *
 * Очередь читается заново на каждое её изменение (`onOutboxChange`, отказ, снятие): записи уходят и в фоновом
 * проходе, и с экрана задания — список не должен показывать уже отправленное.
 */
export function useRecordQueue() {
  const [items, setItems] = useState<OutboxItem[]>([]);
  const [rejected, setRejected] = useState<RejectedRecord[]>(() => listRejectedRecords());
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    void queuedOfKind(LESSON_AUDIO_KIND).then(setItems);
  }, []);

  useEffect(() => {
    reload();
    const offs = [
      onOutboxChange(reload),
      onOutboxFailed(reload),
      onOutboxSettled(reload),
      onRejectedRecordsChange(() => setRejected(listRejectedRecords())),
      subscribeSendProgress(() => setTick((n) => n + 1)),
    ];
    return () => offs.forEach((off) => off());
  }, [reload]);

  const rows: RecordQueueView[] = useMemo(() => {
    void tick; // ход отправки меняется без изменения очереди
    return recordQueueRows(items).map((r) => {
      const p = sendProgressOf(r.blockId);
      return { ...r, sending: !!p, percent: p ? p.percent : null };
    });
  }, [items, tick]);

  /** «Отправить сейчас» / «Сдать». Без связи — честное «отправится сама», запись остаётся. */
  const sendOne = useCallback(async (id: string): Promise<SendResult> => {
    if (!isOnline()) return 'offline';
    const done = await flushOne(id);
    return done.includes(id) ? 'sent' : 'queued';
  }, []);

  const sendAll = useCallback(async (): Promise<SendResult> => {
    if (!isOnline()) return 'offline';
    await flush();
    return (await queuedOfKind(LESSON_AUDIO_KIND)).length === 0 ? 'sent' : 'queued';
  }, []);

  /** Удалить с устройства. Подтверждение — у вызывающего (`useConfirm`): хук только делает. */
  const remove = useCallback(async (id: string) => {
    await removeQueued(id);
  }, []);

  /** Байты записи для «Прослушать» — из очереди или из файла на телефоне. */
  const audioUrl = useCallback(async (id: string): Promise<string | null> => {
    const item = await queuedItem(id);
    if (!item) return null;
    const payload = item.payload as LessonAudioPayload;
    try {
      const bytes = await loadLessonAudio(payload);
      return URL.createObjectURL(new Blob([bytes], { type: payload.mime || 'audio/webm' }));
    } catch {
      return null;
    }
  }, []);

  return { rows, rejected, sendOne, sendAll, remove, dismiss: dismissRejectedRecord, audioUrl };
}
